// src/pages/admin/SubBranchList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import DataTable from "components/QuestDataTable";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import FlagLoader from "layouts/flagloader";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const SubBranchList = () => {
  const { isAuthReady, permissions = [], userPermission = [], user: authUser } = useAuth();
  const [subBranches, setSubBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({ q: "", branch_id: "", status: "", owner: "" });
  const [branchOptions, setBranchOptions] = useState([]);

  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasSubBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );
  const canUseOwnerFilter = authUser?.actor_type === "user" && Number(authUser?.vendor_id || 0) <= 0;

  const canViewPage = hasSubBranchPermission("sub_branch.pages.list.view");
  const canCreate =
    hasSubBranchPermission("sub_branch.pages.create.view") &&
    hasSubBranchPermission("sub_branch.actions.open_create");
  const canOpenTrashed =
    hasSubBranchPermission("sub_branch.pages.trashed.view") &&
    hasSubBranchPermission("sub_branch.actions.open_trashed");
  const canOpenEdit =
    hasSubBranchPermission("sub_branch.pages.edit.view") &&
    hasSubBranchPermission("sub_branch.actions.open_edit");
  const canDelete = hasSubBranchPermission("sub_branch.actions.delete");
  const canConfirmDelete = hasSubBranchPermission("sub_branch.actions.confirm_delete");
  const canCancelDelete = hasSubBranchPermission("sub_branch.actions.cancel_delete");
  const canViewField = useCallback(
    (field) => hasSubBranchPermission(`sub_branch.fields.${field}.view`),
    [hasSubBranchPermission]
  );

  const queryString = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "owner" && !canUseOwnerFilter) return;
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });
    return params.toString();
  }, [canUseOwnerFilter, filters]);

  useEffect(() => {
    if (canUseOwnerFilter || !filters.owner) return;
    setFilters((prev) => ({ ...prev, owner: "" }));
  }, [canUseOwnerFilter, filters.owner]);

  const fetchSubBranches = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const qs = queryString();
      const res = await fetch(`${API_URL}/sub-branches${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch sub branches");
      }

      const { data } = await res.json();
      setSubBranches(data?.data || data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading sub branches",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  const loadBranchOptions = useCallback(async () => {
    if (branchOptions.length > 0) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/sub-branches/branch-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBranchOptions(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setBranchOptions([]);
    }
  }, [branchOptions.length]);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchSubBranches();
  }, [canViewPage, fetchSubBranches, isAuthReady]);

  const handleDeleteClick = (item) => {
    if (!canDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to delete sub branches.",
        color: "error",
      });
      return;
    }
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (item) => {
    if (!canOpenEdit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to edit sub branches.",
        color: "error",
      });
      return;
    }
    navigate(`/sub-branches/edit/${item.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to confirm delete.",
        color: "error",
      });
      return;
    }

    if (!selectedItem) return;

    try {
      setDeletingId(selectedItem.id);

      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/sub-branches/${selectedItem.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Delete failed");
      }

      setSnackbar({
        open: true,
        message: "Sub Branch deleted successfully",
        color: "success",
      });

      fetchSubBranches();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Delete failed",
        color: "error",
      });
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!canCancelDelete) return;
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const qs = queryString();
      const res = await fetch(`${API_URL}/sub-branches/export${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Export failed");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sub-branches.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Export failed", color: "error" });
    }
  };

  const columns = [];
  if (canViewField("name")) columns.push({ Header: "Sub Branch Name", accessor: "name" });
  if (canViewField("code")) columns.push({ Header: "Code", accessor: "code" });
  if (canViewField("branch")) columns.push({ Header: "Branch", accessor: "branch_name" });
  if (canViewField("address")) columns.push({ Header: "Address", accessor: "address" });
  if (canViewField("status")) columns.push({ Header: "Status", accessor: "status" });
  if (canViewField("actions") && (canOpenEdit || canDelete)) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = subBranches.map((item) => ({
    branch_name: item.branch?.name || "-",
    name: item.name,
    code: item.code || "-",
    address: item.address || "-",
    status: (
      <SoftBox px={1.5} py={0.5}>
        <SoftTypography variant="caption" color={item.status ? "info" : "error"}>
          {item.status ? "ACTIVE" : "INACTIVE"}
        </SoftTypography>
      </SoftBox>
    ),
    actions: (
      <>
        {canOpenEdit && (
          <Tooltip title="Edit Sub Branch">
            <IconButton size="small" onClick={() => handleEditClick(item)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {canDelete && (
          <Tooltip title="Delete Sub Branch">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(item)}
              disabled={deletingId === item.id}
            >
              {deletingId === item.id ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="70vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Sub Branch List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view sub branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Sub Branch Management"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" mb={3}>
          <SoftTypography variant="h4">Sub Branch List</SoftTypography>

          <SoftBox display="flex" gap={1}>
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="warning"
                startIcon={<Icon>delete</Icon>}
                onClick={() => navigate("/sub-branches/trashed")}
              >
                Trash
              </SoftButton>
            )}

            <SoftButton
              size="small"
              color="dark"
              variant="outlined"
              startIcon={<Icon>download</Icon>}
              onClick={handleExport}
            >
              Export
            </SoftButton>

            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/sub-branches/add")}
              >
                Add Sub Branch
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
          <SoftBox width={{ xs: "100%", sm: "220px" }}>
            <SoftInput
              size="small"
              placeholder="Search sub branches"
              value={filters.q}
              onChange={handleFilterChange("q")}
            />
          </SoftBox>
          <select
            value={filters.branch_id}
            onFocus={loadBranchOptions}
            onChange={handleFilterChange("branch_id")}
            style={{ minHeight: 38, minWidth: 180, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Branches</option>
            {branchOptions.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={handleFilterChange("status")}
            style={{ minHeight: 38, minWidth: 150, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {canUseOwnerFilter && (
            <select
              value={filters.owner}
              onChange={handleFilterChange("owner")}
              style={{ minHeight: 38, minWidth: 180, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
            >
              <option value="">All Owners</option>
              <option value="admin">Admin</option>
              <option value="vendor">Internal Vendor</option>
            </select>
          )}
        </SoftBox>

        <Card>
          {columns.length > 0 ? (
            <DataTable table={{ columns, rows }} />
          ) : (
            <SoftBox p={3}>
              <SoftTypography variant="body2" color="text">
                No visible fields are assigned for this page.
              </SoftTypography>
            </SoftBox>
          )}
        </Card>
      </SoftBox>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>

        <DialogContent>
          <SoftTypography>
            Delete <strong>{selectedItem?.name}</strong>?
          </SoftTypography>
        </DialogContent>

        <DialogActions>
          {canConfirmDelete && (
            <SoftButton
              onClick={handleDeleteConfirm}
              color="info"
              variant="contained"
              disabled={deletingId === selectedItem?.id}
              endIcon={deletingId === selectedItem?.id ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {deletingId === selectedItem?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}

          {canCancelDelete && (
            <SoftButton color="error" variant="contained" onClick={closeDeleteDialog}>
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default SubBranchList;
